/**
 * PROTOCOL BUMP CHECKLIST: 1) Pick the new version: YYYY.MM.DD.minor (same day? bump minor, new
 * day? reset minor to 0). 2) Update InterviewContracts.kt: Bump ProtocolVersions.CURRENT and
 * CURRENT_WIRE_STRING. 3) Update ProtocolSupportPolicy.kt: Set MIN/MAX supported ranges. 4) Run
 * Tests: ProtocolSupportPolicyTest, ProtocolGateRoutingTest, SessionViewModelTurnRunnerTest.
 */
package interviewprep.common

import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable

const val CLIENT_PROTOCOL_HEADER = "X-Client-Protocol"
const val DEV_PREMIUM_OVERRIDE_HEADER = "X-Dev-Premium-Override"

@Serializable
data class ProtocolVersion(val year: Int, val month: Int, val day: Int, val minor: Int) :
        Comparable<ProtocolVersion> {
        init {
                require(year >= 2000) { "year must be >= 2000" }
                require(month in 1..12) { "month must be 1..12" }
                require(day in 1..31) { "day must be 1..31" }
                require(minor >= 0) { "minor must be >= 0" }
        }

        override fun compareTo(other: ProtocolVersion): Int {
                return compareValuesBy(
                        this,
                        other,
                        ProtocolVersion::year,
                        ProtocolVersion::month,
                        ProtocolVersion::day,
                        ProtocolVersion::minor
                )
        }

        override fun toString(): String {
                return year.toString().padStart(4, '0') +
                        "." +
                        month.toString().padStart(2, '0') +
                        "." +
                        day.toString().padStart(2, '0') +
                        "." +
                        minor
        }

        companion object {
                private val WIRE_PATTERN = Regex("""^(\d{4})\.(\d{2})\.(\d{2})\.(\d+)$""")

                fun parseOrNull(raw: String?): ProtocolVersion? {
                        val normalized = raw?.trim().orEmpty()
                        val match = WIRE_PATTERN.matchEntire(normalized) ?: return null
                        val (yearPart, monthPart, dayPart, minorPart) = match.destructured
                        val year = yearPart.toIntOrNull() ?: return null
                        val month = monthPart.toIntOrNull() ?: return null
                        val day = dayPart.toIntOrNull() ?: return null
                        val minor = minorPart.toIntOrNull() ?: return null
                        return runCatching {
                                        ProtocolVersion(
                                                year = year,
                                                month = month,
                                                day = day,
                                                minor = minor
                                        )
                                }
                                .getOrNull()
                }
        }
}

object ProtocolVersions {
        // Protocol version tracks server/client contract compatibility.
        // It is intentionally explicit (not derived from app build/version values).
        val CURRENT: ProtocolVersion = ProtocolVersion(2026, 3, 5, 0)
        val CURRENT_WIRE_STRING: String = CURRENT.toString()
}

/**
 * Single source of truth for client protocol wire value. Rule: Single Provider Pattern - all client
 * code (headers + body) must use this. This prevents header/body protocol drift which would cause
 * 426 errors on /turn.
 */
object ClientProtocolWire {
        val value: String = ProtocolVersions.CURRENT_WIRE_STRING
}

@Serializable
data class QuestionItem(
        val id: String,
        val title: String,
        val sourceTitle: String,
        val content: String,
        val sourcePath: String
)

@Serializable data class QuestionCount(val total: Int)

@Serializable
data class HistoryEntry(
        val timestamp: String,
        val itemId: String,
        val title: String,
        val result: String, // "pass" or "fail"
        val notes: String? = null,
        val confidence: Int? = null // 1-5 scale
)

@Serializable data class SessionHistory(val history: List<HistoryEntry>)

@Serializable
data class HistorySummary(
        val total: Int,
        val lastTimestamp: String? = null,
        val lastItemId: String? = null,
        val lastTitle: String? = null,
        val lastResult: String? = null,
        val recentFails: Int = 0
)

@Serializable
data class AgentNextRequest(val sessionId: String? = null, val promptOverride: String? = null)

@Serializable
data class AgentNextResponse(
        val sessionId: String? = null,
        val prompt: String,
        val command: List<String>,
        val exitCode: Int,
        val stdout: String,
        val stderr: String,
        val assistantMessage: String? = null,
        val stateFile: String,
        val stateSummaryBefore: HistorySummary,
        val stateSummaryAfter: HistorySummary,
        val latestEntryBefore: HistoryEntry? = null,
        val latestEntryAfter: HistoryEntry? = null
)

@Serializable
data class AgentPreviewResponse(
        val prompt: String,
        val command: List<String>,
        val stateFile: String
)

@Serializable
data class SessionStartRequest(
        val userId: String,
        val track: String? = null,
        val level: String? = null,
        val practiceMode: PracticeMode = PracticeMode.STANDARD,
        val careerFocusPath: String? = null,
        val tzOffsetMinutes: Int? = null,
        val appUserId: String? = null,
        val deviceId: String? = null,
        val deviceModel: String? = null,
        val appVersion: String? = null,
        val appBuildNumber: String? = null
)

@Serializable
data class SessionStartResponse(
        val sessionId: String,
        val startedAt: String,
        val nextQuestion: QuestionPrompt
)

@Serializable
data class SessionStartResponseV3(
        val sessionId: String,
        val startedAt: String,
        val nextQuestion: QuestionPrompt,
        val session: SessionSnapshotV1
)

@Serializable
data class SessionSettingsUpdateRequest(
        val practiceMode: PracticeMode
)

@Serializable
data class SessionSettingsUpdateResponse(
        val session: SessionSnapshotV1
)

@Serializable
data class QuestionPrompt(
        val questionId: String,
        val title: String = "",
        val sourceTitle: String = "",
        val prompt: String,
        val sourcePath: String,
        val tags: List<String> = emptyList()
)

@Serializable
enum class ClientEvent {
        START,
        RESUME,
        ANSWER_SUBMIT,
        CLARIFY,
        REPEAT,
        EXAMPLE,
        SKIP
}

@Serializable
enum class InputMode {
        VOICE,
        TEXT
}

@Serializable
data class TurnPayload(
        val answerText: String? = null,
        val answerLanguage: String? = "en",
        val elapsedMs: Long? = null
)

@Serializable
data class ClientContext(
        val inputMode: InputMode,
        val clientTs: String? = null,
        val clientProtocol: String = ""
)

@Serializable
data class TurnCommandV2(
        val requestId: String,
        val event: ClientEvent,
        val payload: TurnPayload? = null,
        val clientContext: ClientContext
)

@Serializable
data class SessionSnapshotV1(
        val schemaVersion: Int = 1,
        val sessionId: String,
        val userId: String,
        val startedAt: String,
        val track: String? = null,
        val level: String? = null,
        val practiceMode: PracticeMode = PracticeMode.STANDARD,
        val careerFocusPath: String? = null,
        val askedQuestionIds: List<String> = emptyList(),
        val attempts: List<SessionAttempt> = emptyList(),
        val turns: List<SessionSnapshotTurn> = emptyList(),
        val currentQuestion: QuestionPrompt? = null,
        val currentQuestionTitle: String? = null,
        val turnIndex: Int = 0,
        val introDelivered: Boolean = false,
        val roundPlan: List<InterviewRoundType> = emptyList(),
        val currentRoundIndex: Int = 0,
        @SerialName("currentMethodBulletIndex") val currentMethodSegmentIndex: Int = 0,
        @SerialName("completedMethodBulletIndices")
        val completedMethodSegmentIndices: List<Int> = emptyList(),
        val stateVersion: Long = 0,
        val turnContextSummary: String = "",
        val lastInterviewerPrompt: String? = null,
        val lastInterviewerResponseMode: String? = null,
        val lastCandidateTranscript: String? = null
)

@Serializable
data class SessionSnapshotTurn(
        val turnId: String,
        val timestamp: String,
        val question: QuestionPrompt,
        val roundType: InterviewRoundType? = null,
        @SerialName("methodBulletToken") val methodSegmentToken: String? = null,
        val answerText: String,
        val answerLanguage: String,
        val elapsedMs: Long? = null,
        val evaluation: EvaluationResult? = null
)

@Serializable
data class TurnCommandV3(
        val requestId: String,
        val event: ClientEvent,
        val payload: TurnPayload? = null,
        val clientContext: ClientContext,
        val session: SessionSnapshotV1
)

@Serializable
data class VoiceQuestion(
        val questionId: String,
        val title: String? = null,
        val voicePrompt: String,
        val displayPrompt: String = "",
        val isIntroTurn: Boolean = false,
        val fullPrompt: String? = null,
        val sourcePath: String,
        val maxAnswerSeconds: Int = 120
)

@Serializable
enum class ServerPhase {
        ASKING,
        LISTENING,
        FEEDBACK,
        ERROR
}

@Serializable
enum class ClientDirective {
        PLAY_PROMPT,
        START_STT,
        SHOW_FEEDBACK,
        SHOW_ERROR
}

@Serializable data class TurnActionDto(val type: ClientDirective)

fun expectedDirectiveForPhase(phase: ServerPhase): ClientDirective {
        return when (phase) {
                ServerPhase.ASKING -> ClientDirective.PLAY_PROMPT
                ServerPhase.LISTENING -> ClientDirective.START_STT
                ServerPhase.FEEDBACK -> ClientDirective.SHOW_FEEDBACK
                ServerPhase.ERROR -> ClientDirective.SHOW_ERROR
        }
}

@Serializable
enum class TurnTransitionHint {
        NEXT_QUESTION,
        DEEPEN_CURRENT,
        INTERVIEW_COMPLETE
}

@Serializable
enum class DedupMeta {
        HIT,
        MISS,
        WAITED
}

@Serializable
enum class InterviewRoundType {
        CAREER,
        BEHAVIORAL,
        CODING,
        SYSTEM_DESIGN,
        LLD,
        TECHNICAL_DEPTH
}

@Serializable
enum class PendingAction {
        ASK,
        LISTEN,
        EVAL_READY
}

@Serializable
enum class SegmentStatus {
        UPCOMING,
        ACTIVE,
        COMPLETED
}

@Serializable
enum class RoundMethodId {
        STAR,
        IAC,
        // Wire compatibility: keep RAST until all active clients can decode the replacement.
        RAST,
        UMFE,
        CMTI
}

@Serializable
data class SegmentProgressDto(val id: String, val label: String, val status: SegmentStatus)

@Serializable
data class RoundMethodProgressDto(
        val methodId: RoundMethodId,
        @SerialName("bullets") val segments: List<SegmentProgressDto>,
        val activeIndex: Int
)

@Serializable
data class InterviewStateDto(
        val turnIndex: Int,
        val roundType: InterviewRoundType = InterviewRoundType.CODING,
        val roundIndex: Int = 1,
        val pendingAction: PendingAction = PendingAction.ASK,
        val showScore: Boolean = true,
        val methodProgress: RoundMethodProgressDto? = null,
        val stateVersion: Long = 0
)

@Serializable
enum class PracticeMode {
        STANDARD,
        MENTOR
}

@Serializable
data class MentorCoachingDto(
        val knowledgeGap: String,
        val canonicalPoints: List<String>,
        val reasoningUpgrade: String? = null,
        val nextAttemptGuidance: String,
        val improvedAnswer: String? = null
)

@Serializable
data class FeedbackDto(
        val evaluation: EvaluationResult,
        val mentorSpeech: String? = null,
        val mentorCoaching: MentorCoachingDto? = null,
        val context: FeedbackContextDto,
        val transition: TurnTransitionHint
)

@Serializable
data class FeedbackContextDto(
        val evaluatedRoundType: InterviewRoundType,
        val evaluatedRoundIndex: Int,
        val evaluatedQuestionIndex: Int,
        val evaluatedTurnAttemptIndex: Int,
        val evaluatedQuestionId: String,
        val evaluatedTurnId: String
)

@Serializable
enum class ClientPlanType {
        ASK_ONLY,
        LISTEN_ONLY,
        ERROR_ONLY,
        SHOW_FEEDBACK_ONLY,
        SHOW_FEEDBACK_THEN_ASK_AND_LISTEN
}

@Serializable data class ClientPlanDto(val type: ClientPlanType)

@Serializable
data class TurnMetaDto(val traceId: String, val dedup: DedupMeta, val retryable: Boolean)

@Serializable
data class TurnEnvelopeV2(
        val sessionId: String,
        val serverTurnId: String,
        val phase: ServerPhase,
        val actions: List<TurnActionDto>,
        val plan: ClientPlanDto,
        val transitionHint: TurnTransitionHint? = null,
        val question: VoiceQuestion? = null,
        val feedback: FeedbackDto? = null,
        val state: InterviewStateDto,
        val meta: TurnMetaDto
)

@Serializable
data class TurnEnvelopeV3(
        val sessionId: String,
        val serverTurnId: String,
        val phase: ServerPhase,
        val actions: List<TurnActionDto>,
        val plan: ClientPlanDto,
        val transitionHint: TurnTransitionHint? = null,
        val question: VoiceQuestion? = null,
        val feedback: FeedbackDto? = null,
        val uiState: InterviewStateDto,
        val meta: TurnMetaDto,
        val session: SessionSnapshotV1
)

@Serializable
data class StarCoverage(
        val situation: Boolean = false,
        val task: Boolean = false,
        val action: Boolean = false,
        val result: Boolean = false
)

@Serializable
enum class SignalReadiness {
        STILL_PROBING,
        SIGNAL_COMPLETE,
        FAIL_NO_DATA
}

@Serializable
data class EvaluationResult(
        val evaluationId: String,
        val score: Int,
        val pass: Boolean,
        val strengths: List<String>,
        val gaps: List<String>,
        val idealPoints: List<String>,
        val followUpQuestion: String? = null,
        val isExample: Boolean = false,
        val readyToAdvanceRound: Boolean? = null,
        val roundType: InterviewRoundType? = null,
        val starCoverage: StarCoverage? = null,
        val signalReadiness: SignalReadiness = SignalReadiness.STILL_PROBING,
        val coveredSegmentTokens: List<String> = emptyList(),
        val thinking: String? = null
)

val EvaluationResult.hasCompleteSignal: Boolean
        get() = signalReadiness == SignalReadiness.SIGNAL_COMPLETE

val EvaluationResult.hasStrongCompleteSignal: Boolean
        get() = hasCompleteSignal && meetsInterviewStrongSignalScore(score)

fun EvaluationResult.withCanonicalPass(): EvaluationResult {
        val canonicalPass = hasCompleteSignal
        return if (pass == canonicalPass) this else copy(pass = canonicalPass)
}

@Serializable
data class SessionAttempt(
        val attemptId: String,
        val timestamp: String,
        val questionId: String,
        val sourcePath: String,
        val prompt: String,
        val answerText: String,
        val answerLanguage: String? = null,
        val timeSpentSec: Int? = null,
        val evaluation: EvaluationResult
)

@Serializable
data class SessionHistoryResponse(
        val sessionId: String,
        val startedAt: String,
        val userId: String,
        val track: String? = null,
        val level: String? = null,
        val totalAttempts: Int,
        val averageScore: Int,
        val passRate: Int,
        val attempts: List<SessionAttempt>
)

@Serializable
data class ApiError(
        val code: String,
        val message: String,
        val traceId: String? = null,
        val phase: ServerPhase? = null,
        val retryable: Boolean? = null,
        val upgrade: ClientUpgradeRequiredPayload? = null
)

@Serializable
data class ClientUpgradeRequiredPayload(
        val minSupported: String,
        val maxSupported: String,
        val serverProtocol: String,
        val message: String,
        val upgradeUrl: String? = null
)

@Serializable
data class ClientIssueReportRequest(
        val sessionId: String? = null,
        val turnId: String? = null,
        val reason: String? = null,
        val voiceState: String? = null,
        val answerTextSnippet: String? = null,
        val lastSubmittedAnswerSnippet: String? = null,
        val recentLogs: List<String> = emptyList(),
        val sessionSnapshot: SessionSnapshotV1? = null
)

@Serializable
data class ClientIssueReportResponse(
        val reportId: String,
        val sessionId: String,
        val sessionFound: Boolean
)

@Serializable
data class MobileClientLogUploadRequest(
        val sessionId: String? = null,
        val turnId: String? = null,
        val voiceState: String? = null,
        val reason: String? = null,
        val recentLogs: List<String> = emptyList()
)

@Serializable
data class MobileClientLogUploadResponse(val sessionId: String, val acceptedLines: Int)

@Serializable
data class CategoryInfo(
        val id: InterviewRoundType,
        val problemCount: Int
)

@Serializable data class CatalogResponse(val categories: List<CategoryInfo>)

@Serializable
data class CareerFocusFolder(
        val path: String,
        val label: String
)

@Serializable
data class CareerFocusFoldersResponse(
        val folders: List<CareerFocusFolder>
)

@Serializable
data class UserStatusResponse(
        val userId: String,
        val isPro: Boolean,
        val freeRoundsUsed: Int,
        val freeRoundsLimit: Int,
        val isValidationOutage: Boolean = false
)
